/**
 * Staff sign-in: OpenID Connect authorization code flow with PKCE (S256) against the
 * Keycloak realm `macksons` (Req 3.1), using oidc-client-ts (Apache-2.0).
 *
 * Token storage: the signed-in user (access, refresh and id tokens) is kept in memory
 * (`InMemoryWebStorage`), never in localStorage/sessionStorage, so tokens do not survive a
 * reload and are not readable from storage. Only the short-lived sign-in request state
 * (PKCE `code_verifier`, `state`) uses sessionStorage, because it must survive the redirect
 * to Keycloak and back; oidc-client-ts removes it when the callback is processed.
 *
 * After a reload the app restores the session with `signinSilent` (refresh token if still in
 * memory, else a hidden iframe with `prompt=none` against the Keycloak SSO cookie); if that
 * fails the user is sent through `signinRedirect`, which completes without a password
 * prompt while the Keycloak SSO session is alive. Access tokens are renewed shortly before
 * expiry (`automaticSilentRenew`) using the refresh token.
 */
import { InMemoryWebStorage, UserManager, WebStorageStateStore } from 'oidc-client-ts';
export function staffOidcSettings(config) {
    const origin = config.appOrigin.replace(/\/+$/, '');
    return {
        authority: config.authority,
        client_id: config.clientId,
        redirect_uri: `${origin}${config.callbackPath ?? '/auth/callback'}`,
        silent_redirect_uri: `${origin}${config.silentPath ?? '/silent-renew.html'}`,
        post_logout_redirect_uri: `${origin}/`,
        response_type: 'code', // authorization code flow; oidc-client-ts always adds PKCE S256
        scope: 'openid profile email',
        automaticSilentRenew: true,
        silentRequestTimeoutInSeconds: 5,
        // Keycloak session monitoring needs a third-party iframe; renew/401 handling covers expiry.
        monitorSession: false,
        // Fetch roles/claims from the tokens, not the userinfo endpoint.
        loadUserInfo: false,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        ...(config.stateStore ? { stateStore: config.stateStore } : {}),
    };
}
export function createStaffUserManager(config) {
    return new UserManager(staffOidcSettings(config));
}
/**
 * Return target after sign-in. Only same-app absolute paths are accepted (no open
 * redirects); the login and callback routes fall back to `/`.
 */
export function safeReturnTo(value) {
    if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) {
        return '/';
    }
    return value.startsWith('/login') || value.startsWith('/auth/') ? '/' : value;
}
/**
 * Returns a valid, unexpired user, trying one silent sign-in when none is in memory (after
 * a reload) or the token has expired. Concurrent callers share the same attempt.
 */
export function createSessionRestorer(userManager) {
    let pending;
    return async function currentUser() {
        const user = await userManager.getUser();
        if (user && !user.expired)
            return user;
        pending ??= userManager
            .signinSilent()
            .catch(() => null)
            .finally(() => {
            pending = undefined;
        });
        const renewed = await pending;
        return renewed && !renewed.expired ? renewed : null;
    };
}
//# sourceMappingURL=oidc.js.map